import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getTenantAccessSummary } from '../api/tenantAccessApi';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { IconButton } from '../components/ui/IconButton';

function dashboardLabel(t, code, emptyValue) {
  if (!code) {
    return emptyValue;
  }
  return t(`tenantAccess:dashboard.labels.${code}`, { defaultValue: code });
}

export function TenantAccessDashboardPage() {
  const { t } = useTranslation(['tenantAccess', 'common']);
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const emptyValue = t('common:empty_value');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getTenantAccessSummary();
      setSummary(response.data || response);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const data = summary || {};
  const tenants = data.tenants || [];
  const recordCount = tenants.length;

  const metricCards = useMemo(
    () => [
      { key: 'allowed', label: t('tenantAccess:dashboard.metrics.allowed'), value: data.allowed ?? 0 },
      { key: 'trial_active', label: t('tenantAccess:dashboard.metrics.trial_active'), value: data.trial_active ?? 0 },
      { key: 'past_due_grace', label: t('tenantAccess:dashboard.metrics.past_due_grace'), value: data.past_due_grace ?? 0 },
      { key: 'payment_blocked', label: t('tenantAccess:dashboard.metrics.payment_blocked'), value: data.payment_blocked ?? 0 },
      { key: 'suspended_or_canceled', label: t('tenantAccess:dashboard.metrics.suspended_or_canceled'), value: data.suspended_or_canceled ?? 0 },
      { key: 'manual_overrides', label: t('tenantAccess:dashboard.metrics.manual_overrides'), value: data.manual_overrides ?? 0 },
    ],
    [data.allowed, data.manual_overrides, data.past_due_grace, data.payment_blocked, data.suspended_or_canceled, data.trial_active, t],
  );

  const columns = useMemo(
    () => [
      {
        id: 'tenant_name',
        label: t('tenantAccess:dashboard.columns.tenant'),
        render: (row) => row.tenant_name || emptyValue,
      },
      {
        id: 'customer_name',
        label: t('tenantAccess:dashboard.columns.customer'),
        render: (row) => row.customer_name || emptyValue,
      },
      {
        id: 'access_status',
        label: t('tenantAccess:dashboard.columns.access_status'),
        render: (row) => (
          <StatusBadge
            status={row.access_status || 'unknown'}
            category="tenant_access"
            apiLabel={dashboardLabel(t, row.access_status, emptyValue)}
            size="Sm"
          />
        ),
      },
      {
        id: 'reason_code',
        label: t('tenantAccess:dashboard.columns.reason'),
        render: (row) => dashboardLabel(t, row.reason_code, emptyValue),
      },
      {
        id: 'subscription_status',
        label: t('tenantAccess:dashboard.columns.subscription'),
        render: (row) => (
          row.subscription_status
            ? (
              <StatusBadge
                status={row.subscription_status}
                category="subscription"
                apiLabel={dashboardLabel(t, row.subscription_status, emptyValue)}
                size="Sm"
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'override_active',
        label: t('tenantAccess:dashboard.columns.override'),
        render: (row) => (
          row.override_active
            ? (
              <StatusBadge
                status={row.override_type || 'override'}
                category="generic"
                apiLabel={row.override_type || t('common:yes')}
                size="Sm"
                tone="Warning"
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'last_checked_at',
        label: t('tenantAccess:dashboard.columns.last_checked'),
        render: (row) => {
          if (!row.last_checked_at) return emptyValue;
          const date = new Date(row.last_checked_at);
          return Number.isNaN(date.getTime())
            ? row.last_checked_at
            : date.toLocaleString();
        },
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        width: 72,
        render: (row) => (
          <IconButton
            size="Sm"
            tone="Neutral"
            aria-label={t('common:view_detail')}
            onClick={(event) => {
              event.stopPropagation();
              if (row.tenant_id) {
                navigate(`/tenants/${row.tenant_id}`);
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ),
      },
    ],
    [emptyValue, navigate, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('tenantAccess:dashboard.title')}
        subtitle={t('tenantAccess:dashboard.subtitle')}
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={load} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('tenantAccess:dashboard.load_failed')}
        </Alert>
      )}

      {loading ? (
        <>
          <SectionCard variant="Default" sx={{ mb: 2.5 }}>
            <LoadingSkeleton type="CardGrid" rows={6} />
          </SectionCard>
          <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('tenantAccess:dashboard.tenants')}
            />
            <LoadingSkeleton type="Table" embedded rows={3} columns={8} />
          </SectionCard>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(6, minmax(0, 1fr))',
              },
              gap: 2,
              width: '100%',
              mb: 2.5,
            }}
          >
            {metricCards.map((card) => (
              <MetricCard
                key={card.key}
                value={card.value}
                label={card.label}
              />
            ))}
          </Box>

          <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('tenantAccess:dashboard.tenants')}
              meta={(
                <StatusBadge
                  status="list"
                  category="generic"
                  apiLabel={t('tenantAccess:dashboard.toolbar_count', { count: recordCount })}
                  size="Sm"
                  tone="Neutral"
                />
              )}
            />

            <DataTable
              type="Default"
              embedded
              columns={columns}
              rows={tenants}
              getRowKey={(row) => row.tenant_id}
              emptyMessage={t('tenantAccess:dashboard.no_tenants')}
              sx={{
                overflowX: 'auto',
                width: '100%',
                '& table': {
                  width: '100%',
                  minWidth: '100%',
                },
              }}
            />
          </SectionCard>
        </>
      )}
    </Box>
  );
}
